// ==========================================================================
// lib/transactions.ts — imported transactions, coding, transfers (FF-010)
// ==========================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "./supabase";
import { useHouseholdId, useMe } from "./household";
import { fingerprints, sha256, type Mapping, type ParsedRow } from "./csv";

export type Split = { id?: string; amount_cents: number; category_id: string; tracker_id: string | null; asset_id: string | null; note: string | null };
export type Txn = {
  id: string; account_id: string; posted_on: string; amount_cents: number; description: string; balance_cents: number | null;
  status: "new" | "coded" | "excluded"; transfer_pair_id: string | null; note: string | null; contact_id: string | null; card_id: string | null;
  transaction_splits: Split[];
};
export type Profile = { id: string; name: string; mapping: Mapping };

function useInvalidate() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return () => { for (const k of ["txns", "split_totals", "batches", "audit", "unreconciled", "rec_summary", "contact_totals"]) void qc.invalidateQueries({ queryKey: [k, hid] }); };
}

export function useTransactions(from: string, to: string) {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["txns", hid, from, to], enabled: !!hid,
    queryFn: async () => {
      const data: Txn[] = [];
      for (let at = 0; ; at += 1000) {   // the API returns at most 1,000 rows per request
        const { data: page, error } = await supabase.from("transactions")
          .select("id,account_id,posted_on,amount_cents,description,balance_cents,status,transfer_pair_id,note,contact_id,card_id,transaction_splits(id,amount_cents,category_id,tracker_id,asset_id,note)")
          .eq("household_id", hid).is("deleted_at", null).gte("posted_on", from).lte("posted_on", to)
          .order("posted_on", { ascending: false }).order("created_at", { ascending: false }).range(at, at + 999);
        if (error) throw error;
        data.push(...(page as unknown as Txn[]));
        if ((page ?? []).length < 1000) break;
      }
      return data.map((t) => ({ ...t, amount_cents: Number(t.amount_cents), balance_cents: t.balance_cents === null ? null : Number(t.balance_cents), transaction_splits: t.transaction_splits.map((s) => ({ ...s, amount_cents: Number(s.amount_cents) })) }));
    },
  });
}

/** Spending per code and per tracker for a date range (from splits). */
export function useSplitTotals(from: string, to: string) {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["split_totals", hid, from, to], enabled: !!hid,
    queryFn: async () => {
      // Added up in the database (migration 013): the API caps row lists at
      // 1,000, which would quietly short a long period.
      const { data, error } = await supabase.rpc("split_totals", { p_household: hid, p_from: from, p_to: to });
      if (error) throw error;
      const byCat = new Map<string, number>(); const byTracker = new Map<string, number>();
      for (const s of data as { amount_cents: number; category_id: string; tracker_id: string | null }[]) {
        const a = Number(s.amount_cents);
        byCat.set(s.category_id, (byCat.get(s.category_id) ?? 0) + a);
        if (s.tracker_id) byTracker.set(s.tracker_id, (byTracker.get(s.tracker_id) ?? 0) + a);
      }
      return { byCat, byTracker };
    },
  });
}

/** Bank descriptions are "MERCHANT - Visa Purchase - Receipt 123…" (ING) or
 *  "WOOLWORTHS 1234 KEDRON" (others). The merchant is the part before the
 *  first " - "; the rest is shown smaller. */
export function splitDescription(desc: string): { merchant: string; detail: string } {
  const i = desc.indexOf(" - ");
  return i > 0 ? { merchant: desc.slice(0, i).trim(), detail: desc.slice(i + 3).trim() } : { merchant: desc, detail: "" };
}

/** Merchant key for "same as last time": "WOOLWORTHS 1234 KEDRON - Visa…" -> "woolworths kedron". */
export function merchantKey(desc: string) {
  return splitDescription(desc).merchant.toLowerCase().replace(/[^a-z ]+/g, " ").split(/\s+/)
    .filter((w) => w.length > 1 && !["eftpos", "visa", "purchase", "card", "debit", "pty", "ltd", "sq"].includes(w)).slice(0, 3).join(" ");
}

/** The most recent coding for each merchant, from the last 1,000 coded transactions. Used to suggest "same as last time". */
export function useCodingHistory() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["txns", hid, "history"], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("transactions")
        .select("description,posted_on,contact_id,transaction_splits(amount_cents,category_id,tracker_id,asset_id)")
        .eq("household_id", hid).eq("status", "coded").is("deleted_at", null).order("posted_on", { ascending: false }).limit(1000);
      if (error) throw error;
      const last = new Map<string, { category_id: string; tracker_id: string | null; asset_id: string | null; contact_id: string | null }[]>();
      for (const t of data as unknown as { description: string; contact_id: string | null; transaction_splits: Split[] }[]) {
        const k = merchantKey(t.description);
        if (k && !last.has(k) && t.transaction_splits.length === 1) last.set(k, t.transaction_splits.map((s) => ({ category_id: s.category_id, tracker_id: s.tracker_id, asset_id: s.asset_id, contact_id: t.contact_id })));
      }
      return last;
    },
  });
}

export function useProfiles() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["profiles", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("import_profiles").select("id,name,mapping").eq("household_id", hid).order("name");
      if (error) throw error;
      return data as Profile[];
    },
  });
}

export type ImportResult = { added: number; skipped: number; from: string; to: string };

export function useImport() {
  const hid = useHouseholdId(); const me = useMe(); const qc = useQueryClient(); const inv = useInvalidate();
  return useMutation({
    mutationFn: async (a: { accountId: string; fileName: string; fileText: string; rows: ParsedRow[]; mapping: Mapping; profileName: string }): Promise<ImportResult> => {
      const fileSha = await sha256(a.fileText);
      const { data: existing } = await supabase.from("import_batches").select("id").eq("account_id", a.accountId).eq("file_sha256", fileSha).maybeSingle();
      if (existing) throw new Error("This exact file has already been imported into this account.");
      const { data: prof, error: pe } = await supabase.from("import_profiles").upsert({ household_id: hid, name: a.profileName, mapping: a.mapping }, { onConflict: "household_id,name" }).select("id").single();
      if (pe) throw pe;
      const dates = a.rows.map((r) => r.posted_on).sort();
      const { data: batch, error: be } = await supabase.from("import_batches").insert({
        household_id: hid, account_id: a.accountId, profile_id: (prof as { id: string }).id, file_name: a.fileName, file_sha256: fileSha,
        rows_in_file: a.rows.length, date_from: dates[0] ?? null, date_to: dates[dates.length - 1] ?? null, imported_by: me?.id ?? null,
      }).select("id").single();
      if (be) throw be;
      const fps = await fingerprints(a.rows);
      let added = 0;
      for (let i = 0; i < a.rows.length; i += 500) {
        const chunk = a.rows.slice(i, i + 500).map((r, j) => ({ ...r, household_id: hid, account_id: a.accountId, import_batch_id: (batch as { id: string }).id, fingerprint: fps[i + j] }));
        const { data, error } = await supabase.from("transactions").upsert(chunk, { onConflict: "account_id,fingerprint", ignoreDuplicates: true }).select("id");
        if (error) throw error;
        added += (data ?? []).length;
      }
      const skipped = a.rows.length - added;
      // Statement balance from the CSV's balance column: the newest row's balance.
      // Banks list newest-first or oldest-first; the dates tell us which.
      const withBal = a.rows.filter((r) => r.balance_cents !== null);
      if (withBal.length) {
        const newestFirst = withBal[0].posted_on >= withBal[withBal.length - 1].posted_on;
        const last = newestFirst ? withBal[0] : withBal[withBal.length - 1];
        await supabase.from("account_statements").upsert({
          household_id: hid, account_id: a.accountId, statement_date: last.posted_on, closing_balance_cents: last.balance_cents,
          source: "csv", import_batch_id: (batch as { id: string }).id,
        }, { onConflict: "account_id,statement_date" });
      }
      await supabase.from("import_batches").update({ rows_added: added, rows_skipped: skipped }).eq("id", (batch as { id: string }).id);
      void qc.invalidateQueries({ queryKey: ["profiles", hid] });
      return { added, skipped, from: dates[0], to: dates[dates.length - 1] };
    },
    onSuccess: inv,
  });
}

export function useCodeTransaction() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: async (a: { id: string; splits: Split[]; note?: string | null; contactId?: string | null }) => {
      const { error } = await supabase.rpc("code_transaction", {
        p_transaction_id: a.id,
        p_splits: a.splits.map((s) => ({ amount_cents: s.amount_cents, category_id: s.category_id, tracker_id: s.tracker_id ?? "", asset_id: s.asset_id ?? "", note: s.note ?? "" })),
        p_note: a.note ?? null, p_contact_id: a.contactId ?? null,
      });
      if (error) throw error;
    },
    onSuccess: inv,
  });
}

export function usePairTransfer() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: async (a: { id: string; otherId: string | null; previousOther?: string | null }) => {
      if (a.otherId) {
        const { error: e1 } = await supabase.from("transactions").update({ transfer_pair_id: a.otherId }).eq("id", a.id); if (e1) throw e1;
        const { error: e2 } = await supabase.from("transactions").update({ transfer_pair_id: a.id }).eq("id", a.otherId); if (e2) throw e2;
      } else {
        const { error: e1 } = await supabase.from("transactions").update({ transfer_pair_id: null }).eq("id", a.id); if (e1) throw e1;
        if (a.previousOther) { const { error: e2 } = await supabase.from("transactions").update({ transfer_pair_id: null }).eq("id", a.previousOther); if (e2) throw e2; }
      }
    },
    onSuccess: inv,
  });
}

export function useSetStatus() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: async (a: { id: string; status: "new" | "excluded" }) => {
      const { error } = await supabase.from("transactions").update({ status: a.status }).eq("id", a.id);
      if (error) throw error;
    },
    onSuccess: inv,
  });
}

/** Every line for one contact, newest first (paged past the 1,000-row limit). */
export function useContactLines(contactId: string | null, from: string, to: string) {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["txns", hid, "contact", contactId, from, to], enabled: !!hid && !!contactId,
    queryFn: async () => {
      const out: Txn[] = [];
      for (let at = 0; ; at += 1000) {
        const { data, error } = await supabase.from("transactions")
          .select("id,account_id,posted_on,amount_cents,description,balance_cents,status,transfer_pair_id,note,contact_id,card_id,transaction_splits(id,amount_cents,category_id,tracker_id,asset_id,note)")
          .eq("household_id", hid).eq("contact_id", contactId!).is("deleted_at", null).gte("posted_on", from).lte("posted_on", to)
          .order("posted_on", { ascending: false }).range(at, at + 999);
        if (error) throw error;
        out.push(...(data as unknown as Txn[]).map((t) => ({ ...t, amount_cents: Number(t.amount_cents), transaction_splits: t.transaction_splits.map((x) => ({ ...x, amount_cents: Number(x.amount_cents) })) })));
        if ((data ?? []).length < 1000) break;
      }
      return out;
    },
  });
}

/** All coded lines that have a contact, for the health check (just ids, codes, contact). */
export function useContactCoding() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["txns", hid, "contact_coding"], enabled: !!hid,
    queryFn: async () => {
      const out: { id: string; contact_id: string; amount_cents: number; transaction_splits: { category_id: string }[] }[] = [];
      for (let at = 0; ; at += 1000) {
        const { data, error } = await supabase.from("transactions").select("id,contact_id,amount_cents,transaction_splits(category_id)")
          .eq("household_id", hid).eq("status", "coded").not("contact_id", "is", null).is("deleted_at", null).order("posted_on").range(at, at + 999);
        if (error) throw error;
        out.push(...(data as unknown as typeof out));
        if ((data ?? []).length < 1000) break;
      }
      return out;
    },
  });
}
